import { Anime, BROADCAST_DAYS, BroadcastDay } from '../types/anime';
import { areAnimeMatching, hasConflictingSource, withSourceAudio } from './helpers';

/**
 * AnimeRuka's weekly schedule is the source of truth for every saved show's broadcast day/time:
 * whenever it changes, the cards follow. Shows that aren't on it keep their last known time,
 * which other sites may then update. Shows pinned to Ani-One's schedule are left alone.
 */

export interface RukaSlot {
  broadcastDay: BroadcastDay;
  broadcastTime: string;
  nextEp: number;
}

export interface RukaScheduleEntry {
  url: string;
  romajiTitle: string;
  thaiTitle: string;
  sub?: RukaSlot;
  dub?: RukaSlot;
  // Listed under "สัปดาห์นี้งดฉาย / พักฉาย" instead of a day (no sub/dub slot then)
  onBreak?: { label: string; returnOn: string; nextEp: number };
}

const normUrl = (url: string) => url.trim().replace(/\/+$/, '').toLowerCase();
const isRukaLink = (url: string) => url.includes('animeruka.com');

// Title matches are the slow part, so remember them per schedule snapshot
const titleMatchCache = new WeakMap<RukaScheduleEntry[], Map<string, string | null>>();

/** This show's schedule entry: by its AnimeRuka link, else (no link yet) by title */
function findEntry(
  anime: Anime,
  entries: RukaScheduleEntry[],
  byUrl: Map<string, RukaScheduleEntry>
): RukaScheduleEntry | undefined {
  const links = anime.sources.filter((s) => s.type === 'animeruka' || isRukaLink(s.url));
  if (links.length > 0) {
    // Linked to a page that isn't on this week's schedule: keep the last time it had
    for (const s of links) {
      const hit = byUrl.get(normUrl(s.url));
      if (hit) return hit;
    }
    return undefined;
  }

  let cache = titleMatchCache.get(entries);
  if (!cache) {
    cache = new Map();
    titleMatchCache.set(entries, cache);
  }
  const key = `${anime.id}|${anime.title}|${anime.thaiTitle || ''}`;
  if (!cache.has(key)) {
    const hit = entries.find(
      (e) =>
        !hasConflictingSource(anime, { url: e.url, platform: 'animeruka' }) &&
        areAnimeMatching(anime, { title: e.romajiTitle, thaiTitle: e.thaiTitle })
    );
    cache.set(key, hit ? normUrl(hit.url) : null);
  }
  const url = cache.get(key);
  return url ? byUrl.get(url) : undefined;
}

export function applyRukaSchedule(
  list: Anime[],
  entries: RukaScheduleEntry[]
): { list: Anime[]; changed: boolean } {
  if (entries.length === 0) return { list, changed: false };
  const byUrl = new Map(entries.map((e) => [normUrl(e.url), e]));
  let changed = false;

  const next = list.map((anime) => {
    // Pinned to Ani-One's schedule by hand
    if (anime.status === 'จบแล้ว' || anime.scheduleSource === 'anione') return anime;
    const entry = findEntry(anime, entries, byUrl);
    // Dub-only shows follow the dub slot; everything else the sub slot
    const slot = entry
      ? anime.audioType === 'พากย์ไทย' ? entry.dub || entry.sub : entry.sub || entry.dub
      : undefined;
    if (entry?.onBreak && !slot) {
      // Skipping this week: keep its day/time for when it comes back, just mark the break
      const { label, returnOn } = entry.onBreak;
      if (anime.onBreak?.label === label && anime.onBreak.returnOn === returnOn) return anime;
      changed = true;
      return {
        ...anime,
        onBreak: { label, returnOn },
        detectedDayNote: `AnimeRuka: สัปดาห์นี้งดฉาย · ${label}`,
      };
    }
    if (!entry || !slot) {
      // Off AnimeRuka's schedule this week: keep the last time, but let other sites update it
      // until AnimeRuka lists the show again
      if (anime.scheduleSource !== 'animeruka' && !anime.onBreak) return anime;
      changed = true;
      const dayName = BROADCAST_DAYS.find((d) => d.id === anime.broadcastDay)?.nameTh || '';
      return {
        ...anime,
        onBreak: undefined,
        scheduleSource: undefined,
        detectedDayNote: dayName
          ? `ยังไม่มีในตาราง AnimeRuka (เวลาล่าสุด: ${dayName}${anime.broadcastTime ? ` ${anime.broadcastTime} น.` : ''})`
          : anime.detectedDayNote,
      };
    }

    const hasLink = anime.sources.some((s) => normUrl(s.url) === normUrl(entry.url));
    if (
      hasLink &&
      !anime.onBreak &&
      anime.scheduleSource === 'animeruka' &&
      anime.broadcastDay === slot.broadcastDay &&
      anime.broadcastTime === slot.broadcastTime
    ) {
      return anime;
    }

    changed = true;
    const dayName = BROADCAST_DAYS.find((d) => d.id === slot.broadcastDay)?.nameTh || '';
    return {
      ...anime,
      broadcastDay: slot.broadcastDay,
      broadcastTime: slot.broadcastTime,
      scheduleSource: 'animeruka' as const,
      onBreak: undefined,
      detectedDayNote: `ตามตาราง AnimeRuka: ${dayName} ${slot.broadcastTime} น.`,
      sources: hasLink
        ? anime.sources
        : [
            ...anime.sources,
            withSourceAudio({
              type: 'animeruka' as const,
              name: 'AnimeRuka',
              url: entry.url,
              latestEp: Math.max(0, slot.nextEp - 1),
            }),
          ],
    };
  });

  return { list: changed ? next : list, changed };
}
